import {
  parsePhoneNumberFromString,
  isValidPhoneNumber,
  validatePhoneNumberLength,
  getCountryCallingCode,
  Metadata,
} from "libphonenumber-js/min"
import mobileExamples from "libphonenumber-js/mobile/examples"

/** Default country when no dial code is present (MENA primary market). */
export const DEFAULT_PHONE_COUNTRY = "SA"

const maxMobileNationalLengthCache = {}

/**
 * Max national (significant) digits for mobile numbers in a country.
 * Uses libphonenumber mobile examples — same source as valid mobile formats.
 */
export const getMaxMobileNationalLength = country => {
  if (!country) {
    return null
  }
  if (maxMobileNationalLengthCache[country]) {
    return maxMobileNationalLengthCache[country]
  }

  const example = mobileExamples[country]
  if (example) {
    const parsed = parsePhoneNumberFromString(example, country)
    const length = parsed?.nationalNumber?.length || null
    if (length) {
      maxMobileNationalLengthCache[country] = length
      return length
    }
  }

  return null
}

/**
 * Max national digits allowed by the country's numbering plan (all line types).
 * Fallback when mobile example is unavailable.
 */
export const getMaxNationalNumberLength = country => {
  if (!country) {
    return null
  }

  try {
    const metadata = new Metadata()
    metadata.selectNumberingPlan(country)
    const possibleLengths = metadata.numberingPlan.possibleLengths()
    return possibleLengths[possibleLengths.length - 1] || null
  } catch {
    return null
  }
}

export const getValidNationalNumberLength = country =>
  getMaxMobileNationalLength(country) ?? getMaxNationalNumberLength(country)

export const resolvePhoneCountry = (value, defaultCountry = DEFAULT_PHONE_COUNTRY) => {
  const parsed = value ? parsePhoneNumberFromString(value) : null
  return parsed?.country || defaultCountry
}

const buildE164 = (callingCode, nationalNumber) =>
  nationalNumber ? `+${callingCode}${nationalNumber}` : ""

/**
 * Truncate phone value to the max mobile national length for its country.
 * Always returns a normalized E.164 string when possible.
 */
export const trimPhoneToMaxLength = (
  value,
  defaultCountry = DEFAULT_PHONE_COUNTRY
) => {
  const input = value || ""
  if (!input) {
    return ""
  }

  const country = resolvePhoneCountry(input, defaultCountry)
  const maxNationalLength = getValidNationalNumberLength(country)
  if (!maxNationalLength) {
    return input
  }

  const parsed = parsePhoneNumberFromString(input, country)
  if (!parsed?.nationalNumber) {
    return input
  }

  const effectiveCountry = parsed.country || country
  const maxForCountry =
    getValidNationalNumberLength(effectiveCountry) || maxNationalLength

  let { nationalNumber, countryCallingCode } = parsed
  if (nationalNumber.length > maxForCountry) {
    nationalNumber = nationalNumber.slice(0, maxForCountry)
  }

  return buildE164(countryCallingCode, nationalNumber)
}

/**
 * Normalize stored phone values for react-phone-number-input (E.164 when possible).
 */
export const normalizePhoneValue = (
  value,
  defaultCountry = DEFAULT_PHONE_COUNTRY
) => {
  if (!value || typeof value !== "string") {
    return ""
  }
  const trimmed = value.trim()
  if (!trimmed) {
    return ""
  }
  if (trimmed.startsWith("+")) {
    return trimPhoneToMaxLength(trimmed, defaultCountry)
  }
  if (/^\d+$/.test(trimmed)) {
    const parsedInternational = parsePhoneNumberFromString(`+${trimmed}`)
    if (parsedInternational?.number) {
      return trimPhoneToMaxLength(parsedInternational.number, defaultCountry)
    }
  }
  const parsed = parsePhoneNumberFromString(trimmed, defaultCountry)
  return trimPhoneToMaxLength(parsed?.number || trimmed, defaultCountry)
}

export const isValidPhoneValue = value => {
  if (!value || typeof value !== "string" || !value.trim()) {
    return true
  }
  try {
    return isValidPhoneNumber(value)
  } catch {
    return false
  }
}

export const getMaxE164Length = (country = DEFAULT_PHONE_COUNTRY) => {
  const nationalMax = getValidNationalNumberLength(country)
  if (!nationalMax) {
    return undefined
  }
  try {
    const callingCode = getCountryCallingCode(country)
    return 1 + String(callingCode).length + nationalMax
  } catch {
    return undefined
  }
}

export const exceedsNationalNumberLimit = (value, country) => {
  const parsed = parsePhoneNumberFromString(value || "")
  if (!parsed?.nationalNumber) {
    return false
  }

  const effectiveCountry = parsed.country || country
  const maxNationalLength = getValidNationalNumberLength(effectiveCountry)
  if (!maxNationalLength) {
    return false
  }

  return parsed.nationalNumber.length > maxNationalLength
}

/**
 * Enforces per-country mobile digit limits on every change (typing or paste).
 */
export const sanitizePhoneInput = (
  nextValue,
  _currentValue = "",
  defaultCountry = DEFAULT_PHONE_COUNTRY
) => trimPhoneToMaxLength(nextValue, defaultCountry)

export const getPhoneLengthError = value => {
  const trimmed = String(value || "").trim()
  if (!trimmed) {
    return null
  }
  return validatePhoneNumberLength(trimmed) || null
}

export const isAtMaxPhoneLength = (
  value,
  defaultCountry = DEFAULT_PHONE_COUNTRY
) => {
  const country = resolvePhoneCountry(value, defaultCountry)
  const maxNationalLength = getValidNationalNumberLength(country)
  if (!maxNationalLength) {
    return false
  }

  const parsed = parsePhoneNumberFromString(value || "", country)
  return (parsed?.nationalNumber?.length || 0) >= maxNationalLength
}
